import React, { Fragment } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { useLabels } from '@confluence/editor-features';
import { usePublicLinkPageDataProvider } from '@confluence/public-links';
import type { PublishProps } from '@confluence/editor-features/entry-points/types';
import type { FlagsStateContainer } from '@confluence/flags';
import type { RetrieveEditorTitleAndContent } from '@confluence/publish-page-as-blog';

import { useEmojiTitleCoverPictureAndContentState } from './hooks/useEmojiTitleCoverPictureAndContentState';
import { useRestrictions } from './hooks/useRestrictions';
import { EditorPrimaryToolbarComponents } from './EditorPrimaryToolbarComponents';

export type EditorPrimaryToolbarComponentsWrapperProps = {
	areMainButtonsDisabled: boolean;
	forceDisabled?: boolean;
	userOffline: boolean;
	collabLimitReached: boolean;
	contentId: string;
	contentType: string;
	isContentCreationPanelOpen: boolean;
	isUnpublishedDraft: boolean;
	onClickPublish: (arg: PublishProps) => () => void;
	onClickOpenContentCreationPanel: () => void;
	onPreviewClicked: (arg?: any) => void;
	saveDraft: (arg?: any) => Promise<void>;
	spaceKey: string;
	title: string | null;
	showRevertDialog: () => void;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	isPublishingContent: boolean;
	editorActions: EditorActions;
	isSingleUserSession: boolean;
	navigateOnClose: () => void;
	triggerAnalyticsEvent: (arg: string, extraData?: { [key: string]: any }) => void;
	shouldRenderPreview: boolean;
	shouldShowRecentSpotlight: boolean;
	checkIfConditionPreventingPublish?: () => Promise<boolean>;
	flags: FlagsStateContainer;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	viewMode?: boolean;
};

export const EditorPrimaryToolbarComponentsWrapper = ({
	areMainButtonsDisabled,
	forceDisabled,
	userOffline,
	collabLimitReached,
	contentId,
	contentType,
	isContentCreationPanelOpen,
	isUnpublishedDraft,
	onClickPublish,
	onClickOpenContentCreationPanel,
	onPreviewClicked,
	saveDraft,
	spaceKey,
	title,
	showRevertDialog,
	retrieveEditorTitleAndContent,
	isPublishingContent,
	editorActions,
	isSingleUserSession,
	navigateOnClose,
	triggerAnalyticsEvent,
	shouldRenderPreview,
	shouldShowRecentSpotlight,
	flags,
	createAnalyticsEvent,
	viewMode,
}: EditorPrimaryToolbarComponentsWrapperProps) => {
	const {
		emojiCoverPictureAndContentStateSubscription,
		hasEmoji,
		hasCoverPicture,
		hasContentState,
	} = useEmojiTitleCoverPictureAndContentState();

	const { hasRestrictionsSubscription, hasRestrictions } = useRestrictions();
	const publicLinkData = usePublicLinkPageDataProvider({
		pageId: contentId,
		fetchPolicy: 'cache-and-network',
	});
	const isPageExternallyShared =
		!publicLinkData.isLoading && publicLinkData.isPublicLinkFullyEnabled;

	const hasLabels = useLabels(contentId);

	return (
		<Fragment>
			{emojiCoverPictureAndContentStateSubscription}
			{hasRestrictionsSubscription}
			<EditorPrimaryToolbarComponents
				areMainButtonsDisabled={areMainButtonsDisabled}
				forceDisabled={forceDisabled}
				userOffline={userOffline}
				collabLimitReached={collabLimitReached}
				contentId={contentId}
				contentType={contentType}
				isContentCreationPanelOpen={isContentCreationPanelOpen}
				isUnpublishedDraft={isUnpublishedDraft}
				onClickPublish={onClickPublish}
				onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
				onPreviewClicked={onPreviewClicked}
				saveDraft={saveDraft}
				spaceKey={spaceKey}
				title={title}
				showRevertDialog={showRevertDialog}
				retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
				isPublishingContent={isPublishingContent}
				editorActions={editorActions}
				isSingleUserSession={isSingleUserSession}
				hasCoverPicture={hasCoverPicture}
				hasEmoji={hasEmoji}
				hasContentState={hasContentState}
				hasRestrictions={hasRestrictions}
				hasLabels={hasLabels}
				navigateOnClose={navigateOnClose}
				triggerAnalyticsEvent={triggerAnalyticsEvent}
				shouldRenderPreview={shouldRenderPreview}
				shouldShowRecentSpotlight={shouldShowRecentSpotlight}
				flags={flags}
				isPageExternallyShared={isPageExternallyShared}
				createAnalyticsEvent={createAnalyticsEvent}
				viewMode={viewMode}
			/>
		</Fragment>
	);
};
